import React, { Component, ReactNode } from 'react';
import { 
  createEntitlementErrorResponse, 
  EntitlementError,
  shouldRetryEntitlementError 
} from '../utils/entitlementErrorHandler';

interface EntitlementErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: EntitlementError, resetError: () => void, suggestions: string[]) => ReactNode;
  onError?: (error: EntitlementError, errorInfo: React.ErrorInfo) => void;
}

interface EntitlementErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  entitlementError: EntitlementError | null;
  suggestions: string[];
  retryCount: number;
}

/**
 * Error boundary specifically for entitlement loading failures
 * Provides graceful fallback UI when premium status cannot be loaded
 */
export class EntitlementErrorBoundary extends Component<
  EntitlementErrorBoundaryProps,
  EntitlementErrorBoundaryState
> {
  private retryTimeout: NodeJS.Timeout | null = null;

  constructor(props: EntitlementErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      entitlementError: null,
      suggestions: [],
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<EntitlementErrorBoundaryState> {
    const errorResponse = createEntitlementErrorResponse(error, {
      component: 'EntitlementErrorBoundary',
      timestamp: new Date().toISOString()
    });

    return {
      hasError: true,
      error,
      entitlementError: errorResponse.error,
      suggestions: errorResponse.suggestions
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Entitlement error boundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError && this.state.entitlementError) {
      this.props.onError(this.state.entitlementError, errorInfo);
    }
  }

  componentWillUnmount() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
    }
  }

  resetError = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      entitlementError: null,
      suggestions: [],
      retryCount: 0
    });
  };

  handleAutoRetry = () => {
    if (!this.state.entitlementError || !shouldRetryEntitlementError(this.state.entitlementError)) {
      return;
    }

    const maxRetries = 3;
    if (this.state.retryCount >= maxRetries) {
      return;
    }

    // Calculate retry delay (exponential backoff)
    const delay = Math.min(1000 * Math.pow(2, this.state.retryCount), 10000);
    
    this.retryTimeout = setTimeout(() => {
      // Clear the error but keep counting attempts (resetError zeroes the count)
      this.setState(prevState => ({
        hasError: false,
        error: null,
        entitlementError: null,
        suggestions: [],
        retryCount: prevState.retryCount + 1
      }));
    }, delay);
  };

  render() {
    if (this.state.hasError && this.state.entitlementError) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.entitlementError, this.resetError, this.state.suggestions);
      }

      // Default fallback UI with enhanced error handling
      const { entitlementError, suggestions, retryCount } = this.state;
      const canRetry = shouldRetryEntitlementError(entitlementError) && retryCount < 3;

      return (
        <div className="p-4 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-red-600 dark:text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Premium status unavailable
              </h3>
              <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                {entitlementError.userMessage}
              </p>
              
              {/* Recovery suggestions */}
              {suggestions.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-red-800 dark:text-red-200 mb-1">
                    Try these solutions:
                  </p>
                  <ul className="text-xs text-red-700 dark:text-red-300 space-y-1">
                    {suggestions.slice(0, 3).map((suggestion, index) => (
                      <li key={index} className="flex items-center gap-1">
                        <span className="w-1 h-1 bg-red-600 dark:bg-red-400 rounded-full flex-shrink-0"></span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action buttons */}
              <div className="mt-4 flex gap-2">
                <button
                  onClick={this.resetError}
                  className="text-sm font-medium text-red-800 dark:text-red-200 hover:text-red-900 dark:hover:text-red-100 underline"
                >
                  Try Again
                </button>
                
                {canRetry && (
                  <button
                    onClick={this.handleAutoRetry}
                    className="text-sm font-medium text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-200"
                  >
                    Auto Retry ({3 - retryCount} left)
                  </button>
                )}
                
                {!entitlementError.recoverable && (
                  <button
                    onClick={() => window.location.reload()}
                    className="text-sm font-medium text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-200"
                  >
                    Refresh Page
                  </button>
                )}
              </div>

              {/* Error details for debugging (only in development) */}
              {process.env.NODE_ENV === 'development' && (
                <details className="mt-3">
                  <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer">
                    Error Details (Development)
                  </summary>
                  <pre className="mt-1 text-xs text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/40 p-2 rounded overflow-auto">
                    {JSON.stringify({
                      code: entitlementError.code,
                      message: entitlementError.message,
                      retryCount,
                      originalError: this.state.error?.message
                    }, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}